"use client"

import Image, { ImageProps } from 'next/image';
import { useState, useEffect } from 'react';
// Removendo a importação não utilizada
// import { getOptimizedImageUrl } from '@/lib/imageOptimizer';

// Definindo tipos mais específicos em vez de any
type ImageSource = string | { src: string; [key: string]: unknown };

interface OptimizedImageProps extends Omit<ImageProps, 'src'> {
  src: ImageSource;
  fallbackSrc?: ImageSource;
  useWebp?: boolean;
  isLCP?: boolean;
  quality?: number;
}

/**
 * Componente otimizado para carregar imagens respeitando as boas práticas de Web Vitals
 * - Suporte para fallback em caso de erro
 * - Mantém aspect ratio correto para evitar CLS
 * - Configura prioridade para imagens LCP
 * - Lazy loading inteligente para imagens fora da viewport
 * - Converte imagens para WebP quando possível
 */
export default function OptimizedImage({
  src,
  fallbackSrc,
  alt,
  width,
  height,
  useWebp = true,
  isLCP = false,
  // quality é usado indiretamente na transformação WebP
  ...props
}: OptimizedImageProps) {
  // Função para processar qualquer tipo de imagem
  const processImageSrc = (imageSrc: ImageSource): string => {
    if (!imageSrc) return '';
    
    // Se for um objeto com propriedade src (como StaticImport)
    if (typeof imageSrc === 'object' && imageSrc !== null && 'src' in imageSrc) {
      return imageSrc.src;
    }
    
    // Se for string
    return String(imageSrc);
  };

  // Obter URLs processadas
  const baseImageSrc = processImageSrc(src);
  const fallbackImageSrc = fallbackSrc ? processImageSrc(fallbackSrc) : '';
  
  // Desativar WebP se explicitamente definido como false
  const [imgSrc, setImgSrc] = useState(baseImageSrc);
  const [isLoaded, setIsLoaded] = useState(false);
  
  // Reset estado se a source mudar
  useEffect(() => {
    setImgSrc(baseImageSrc);
  }, [baseImageSrc]);

  // Verificar se deve usar WebP
  const shouldUseWebP = (): boolean => {
    // Verificação mais robusta de suporte a WebP no navegador
    try {
      // Primeira verificação: feature detection
      const webpSupport = typeof window !== 'undefined' && 
        document.createElement('canvas')
          .toDataURL('image/webp')
          .startsWith('data:image/webp');
      
      if (process.env.NODE_ENV === 'development') {
        console.log('Suporte a WebP detectado:', webpSupport);
      }
      
      return useWebp && webpSupport;
    } catch {
      console.warn('Erro ao verificar suporte a WebP');
      return false;
    }
  };

  // Obter URL para WebP se disponível e suportado
  const getSrcWithFormat = (imageSrc: string): string => {
    if (!useWebp || !imageSrc || typeof imageSrc !== 'string') return imageSrc;
    
    // Garantir que a URL existe e é válida antes de tentar converter
    try {
      // Verificar se a URL já é WebP
      if (imageSrc.toLowerCase().endsWith('.webp')) return imageSrc;
      
      // Verificar extensões conhecidas
      const matches = imageSrc.match(/\.(jpe?g|png|gif)($|\?)/i);
      if (!matches) return imageSrc; // Não é uma extensão que podemos converter
      
      // Converter para WebP
      const webpUrl = imageSrc.replace(/\.(jpe?g|png|gif)($|\?)/i, '.webp$2');
      
      if (process.env.NODE_ENV === 'development') {
        console.log('Convertendo para WebP:', { 
          original: imageSrc,
          webp: webpUrl
        });
      }
      
      return webpUrl;
    } catch {
      console.warn('Erro ao converter para WebP');
      return imageSrc;
    }
  };

  // Adicionar sempre a URL original como fallback
  const supportsWebP = shouldUseWebP();
  const imgSrcValue = supportsWebP ? getSrcWithFormat(imgSrc) : imgSrc;
  
  // Em desenvolvimento, mostrar se está usando WebP
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Imagem otimizada:', { 
        src: imgSrcValue, 
        original: imgSrc,
        webpAtivado: supportsWebP,
        altura: height,
        largura: width
      });
    }
  }, [imgSrcValue, imgSrc, supportsWebP, height, width]);

  // Calcular o aspect ratio correto
  const calculatedAspectRatio = typeof height === 'number' && typeof width === 'number' ? height / width : undefined;
  
  // Cria classe de estilo para padding baseado no aspect ratio para evitar CLS
  const paddingStyle = calculatedAspectRatio 
    ? { paddingTop: `${calculatedAspectRatio * 100}%` }
    : props.fill ? { paddingTop: '75%' } : undefined; // Default aspect ratio 4:3 para elementos com fill
    
  // Configurações para imagens prioritárias (LCP)
  const priorityProps = isLCP ? {
    priority: true,
    fetchPriority: "high" as const,
    loading: "eager" as const,
  } : props.priority ? {
    priority: true,
    loading: "eager" as const,
  } : {};
  
  // Configurar props para lazy loading
  const lazyProps = !isLCP && !props.priority ? {
    loading: "lazy" as const,
  } : {};

  // Placeholder para imagens não LCP
  const placeholderProps = !isLCP && typeof imgSrc === 'string' && !imgSrc.startsWith('data:') ? {
    placeholder: "blur" as const,
    blurDataURL: "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAoHBwgHBgoICAgLCgoLDhgQDg0NDh0VFhEYIx8lJCIfIiEmKzcvJik0KSEiMEExNDk7Pj4+JS5ESUM8SDc9Pjv/2wBDAQoLCw4NDhwQEBw7KCIoOzs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozv/wAARCAACAAIDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAb/xAAUEAEAAAAAAAAAAAAAAAAAAAAA/8QAFAEBAAAAAAAAAAAAAAAAAAAAAP/EABQRAQAAAAAAAAAAAAAAAAAAAAD/2gAMAwEAAhEDEQA/AKVF//Z",
  } : {};
  
  return (
    <div 
      className={`relative overflow-hidden ${props.className}`} 
      style={{
        ...(paddingStyle || {}),
        // Garantir altura mínima para evitar colapso
        minHeight: props.fill ? '100%' : (typeof height === 'number' ? `${height}px` : undefined)
      }}
    >
      <Image
        src={imgSrcValue}
        alt={alt}
        width={width}
        height={height}
        className={`transition-opacity duration-300 ${
          isLoaded ? 'opacity-100' : 'opacity-0'
        } ${props.fill ? 'object-cover' : ''}`}
        onLoad={() => setIsLoaded(true)}
        onError={() => {
          console.warn('Erro ao carregar imagem:', imgSrcValue);
          if (fallbackImageSrc) {
            setImgSrc(fallbackImageSrc);
          } else if (supportsWebP && imgSrc !== baseImageSrc) {
            // Se falhou com WebP, tenta o formato original
            setImgSrc(baseImageSrc);
          }
        }}
        style={{
          ...props.style,
          // Garantir que a imagem tenha uma altura mínima
          minHeight: props.fill ? '100%' : (typeof height === 'number' ? `${height}px` : undefined)
        }}
        {...priorityProps}
        {...lazyProps}
        {...placeholderProps}
        {...props}
      />
    </div>
  );
} 